import React, {useEffect, useRef} from "react"
import {UseFieldArrayRemove, useFormContext} from "react-hook-form";
import {FieldFlag, FormValues} from "@/features/management-v2/custom-user-fields/types";

type FieldRowProps = {
    index: number
    remove: UseFieldArrayRemove
    isExisting?: boolean
    sirenIdentifierTaken?: boolean
}

const FLAGS: { value: FieldFlag; label: string }[] = [
    { value: "required",         label: "Required"          },
    { value: "unique",           label: "Unique"            },
    { value: "immutable",        label: "Immutable"         },
    { value: "filterable",       label: "Filterable"        },
    { value: "siren_identifier", label: "Siren Identifier"  },
]

export function FieldRow({ index, remove, isExisting = false, sirenIdentifierTaken = false }: FieldRowProps) {
    const { register, watch, setValue } = useFormContext<FormValues>()

    const name = watch(`fields.${index}.name`)
    const key = watch(`fields.${index}.key`)
    const type = watch(`fields.${index}.type`)
    const flags: FieldFlag[] = watch(`fields.${index}.flags`) ?? []

    const hasSirenIdentifier = flags.includes("siren_identifier")
    const sirenPrereqsMet = type === "text" && flags.includes("unique") && flags.includes("immutable")

    const keyManuallyEdited = useRef(false)

    useEffect(() => {
        if (!keyManuallyEdited.current) {
            const generated = slugify(name || "")
            setValue(`fields.${index}.key`, generated)
        }
    }, [name, index, setValue])

    useEffect(() => {
        if (hasSirenIdentifier && !sirenPrereqsMet) {
            setValue(`fields.${index}.flags`, flags.filter(f => f !== "siren_identifier"))
        }
    }, [sirenPrereqsMet, hasSirenIdentifier])

    return (
        <div className="bg-ice border border-light-light-grey rounded-xl p-4 flex flex-col gap-4">

            {/* Inputs */}
            <div className="grid grid-cols-3 gap-3">
                <input
                    placeholder="Field Name"
                    {...register(`fields.${index}.name`)}
                    disabled={isExisting}
                    className="px-3 py-2 border border-light-light-grey rounded-lg bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                />

                <input
                    placeholder="Key"
                    {...register(`fields.${index}.key`, {
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            keyManuallyEdited.current = true
                            e.target.value = sanitizeKey(e.target.value)
                        }
                    })}
                    disabled={isExisting}
                    className="px-3 py-2 border border-light-light-grey rounded-lg bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                />

                <select
                    {...register(`fields.${index}.type`)}
                    disabled={isExisting}
                    className="px-3 py-2 border border-light-light-grey rounded-lg bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                </select>
            </div>

            {/* Flags */}
            <div className="flex flex-wrap gap-2">
                {FLAGS.map(({ value, label }) => {
                    const isFlagDisabled =
                        isExisting ||
                        (value === "siren_identifier" && (sirenIdentifierTaken || !sirenPrereqsMet)) ||
                        (value === "unique" && hasSirenIdentifier) ||
                        (value === "immutable" && hasSirenIdentifier)
                    return (
                        <label
                            key={value}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-light-light-grey text-sm ${isFlagDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-desaturated-grey transition"}`}
                        >
                            <input
                                type="checkbox"
                                checked={flags.includes(value)}
                                disabled={isFlagDisabled}
                                onChange={() => {
                                    const next = flags.includes(value)
                                        ? flags.filter(f => f !== value)
                                        : [...flags, value]
                                    setValue(`fields.${index}.flags`, next)
                                }}
                            />
                            {label}
                        </label>
                    )
                })}
            </div>

            {/* Actions */}
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-dark-red hover:text-red text-sm font-medium"
                >
                    Remove Field
                </button>
            </div>

        </div>
    )
}

function slugify(value: string) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
}

function sanitizeKey(value: string) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, "")
}